import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import classNames from 'classnames';
import Badge from '@atlaskit/badge';
import { LoadingButton } from '@atlaskit/button';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE, getPurchaseMetadataEntries } from '@atlassian/cssxp-utils-entitlement';
import { RegexPatterns } from '@atlassian/cssxp-typescommon';
import { abgGraphqlClient as graphClient } from '@atlassian/temp-client-utils';
import ExportIcon from '@atlaskit/icon/glyph/export';
import {
  ColGroup,
  CustomDynamicResultTable,
  ExpandableRow,
  TableBody,
  TableHead,
  CustomSearch,
  useFeatureToggle,
  SupportTicketNumber,
} from '@css-xp/base';
import { emptyStateProp as defaultEmptyStateProp, errorStateProp, TableHeading } from '@css-xp/ppc-common';
import {
  Order,
  OrderLineItem,
  InvoiceGroup,
  FilterOption,
  OrderConnection,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  routePaths,
  maxThresholdForResults,
  ENABLE_DESIGN_ITERATION_1,
  SEARCH_PAGE_LOADER_OPTIONS,
  STATSIG_FEATURE_GATES,
} from '@css-xp/ppc-constants';
import { emptyStatePropForOrderHistoryTable, emptyStatePropForWrongSearch } from '@css-xp/ppc-common/src/state-props';
import { getRouteUrl, getFilterOptionsForInvoiceGroupsQuery } from '@css-xp/ppc-utils';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { fetchOrdersForTransactionAccountInPPC } from './queries/fetch-orders-for-transaction-account';
import { fetchInvoiceGroupsForSearch } from './queries/fetch-invoice-groups-for-search';
import { fetchCloudSitesForTXA } from './queries/fetch-cloud-sites-for-txa';
import { OrderHistoryTableColumns, OrderHistoryTableProps } from './types';
import { OrderLineItemRow } from './components/order-line-item-row';
import { useFetchOrdersForTransactionAccountPPCQuery } from './use-fetch-orders-for-transaction-account-ppc-query';
import { useOrderHistoryCSV } from './hooks/use-order-history-csv';
import { CONTAINER_PRODUCT_ID } from './constant';
import './order-history-table.scss';

interface FormData {
  searchTerm: string;
}

const getGroupedLineItems = (products: OrderLineItem[], apps: OrderLineItem[]) => {
  const groupedAppIds = new Set<string>();
  const groupedProducts = products.map((product) => {
    const appsForProduct = apps.filter(
      (app) =>
        (app.parentEntitlementId === product.entitlementId || app.parentEntitlementId === product.familyContainerId) &&
        !groupedAppIds.has(app.lineItemId)
    );

    appsForProduct.forEach((app) => groupedAppIds.add(app.lineItemId));
    return {
      ...product,
      apps: appsForProduct,
    };
  });

  const standaloneApps = apps
    .filter(
      (app) =>
        !groupedAppIds.has(app.lineItemId) &&
        !products.some(
          (product) =>
            app.parentEntitlementId === product.entitlementId || app.parentEntitlementId === product.familyContainerId
        )
    )
    .map((app) => ({ ...app }));
  return [...groupedProducts, ...standaloneApps];
};

export const getOrderLineItemRows = ({
  order,
  transactionAccountId,
  invoiceGroup,
  isFlatDiscountSupportEnabled,
  visibleColumns,
  subscriptionCellVersion,
  shouldShowLegacyInvoiceId,
  shouldGroupApps,
  searchedLineItems,
  shouldRenderFrontEndSearchResult,
  isSandboxSupportForSubscriptionsEnabled,
  isGracePeriodVisibilityEnabled,
}: {
  order: Order;
  transactionAccountId: string;
  invoiceGroup?: any;
  isFlatDiscountSupportEnabled?: boolean;
  visibleColumns?: string[];
  subscriptionCellVersion?: number;
  shouldShowLegacyInvoiceId?: boolean;
  shouldGroupApps?: boolean;
  searchedLineItems?: OrderLineItem[];
  shouldRenderFrontEndSearchResult?: boolean;
  isSandboxSupportForSubscriptionsEnabled?: boolean;
  isGracePeriodVisibilityEnabled?: boolean;
}) => {
  const orderLineItems = shouldRenderFrontEndSearchResult
    ? searchedLineItems || []
    : order?.orderLineItems?.nodes || [];
  const filteredLineItems = filterNonFamilyLineItems(orderLineItems as OrderLineItem[]);

  if (shouldGroupApps) {
    const products = filteredLineItems.filter((lineItem) => !lineItem?.parentEntitlementId);
    const apps = filteredLineItems.filter((lineItem) => lineItem?.parentEntitlementId);
    const groupedLineItems = getGroupedLineItems(products, apps);

    return createOrderLineItemRows({
      lineItems: groupedLineItems,
      order,
      transactionAccountId,
      invoiceGroup,
      isFlatDiscountSupportEnabled,
      visibleColumns,
      subscriptionCellVersion,
      shouldShowLegacyInvoiceId,
      shouldGroupApps,
      isSandboxSupportForSubscriptionsEnabled,
      isGracePeriodVisibilityEnabled,
    });
  }
  return createOrderLineItemRows({
    lineItems: filteredLineItems,
    order,
    transactionAccountId,
    invoiceGroup,
    isFlatDiscountSupportEnabled,
    visibleColumns,
    subscriptionCellVersion,
    shouldShowLegacyInvoiceId,
    shouldGroupApps,
    isSandboxSupportForSubscriptionsEnabled,
    isGracePeriodVisibilityEnabled,
  });
};

// Filtering Family line items from the orderLineItems here because it's not available as filter in the query
export const filterNonFamilyLineItems = (orderLineItems: OrderLineItem[]) => {
  const familyEditions = ['Family', 'Family Sandbox'];
  return orderLineItems.filter(
    (lineItem) =>
      !(familyEditions.includes(lineItem?.edition as string) || lineItem?.productId === CONTAINER_PRODUCT_ID)
  );
};

const createOrderLineItemRows = ({
  lineItems,
  order,
  transactionAccountId,
  invoiceGroup,
  isFlatDiscountSupportEnabled,
  visibleColumns,
  subscriptionCellVersion,
  shouldShowLegacyInvoiceId,
  shouldGroupApps = false,
  isSandboxSupportForSubscriptionsEnabled,
  isGracePeriodVisibilityEnabled,
}: {
  lineItems: OrderLineItem[];
  order: Order;
  transactionAccountId: string;
  invoiceGroup?: InvoiceGroup;
  isFlatDiscountSupportEnabled?: boolean;
  visibleColumns?: string[];
  subscriptionCellVersion?: number;
  shouldShowLegacyInvoiceId?: boolean;
  shouldGroupApps?: boolean;
  isSandboxSupportForSubscriptionsEnabled?: boolean;
  isGracePeriodVisibilityEnabled?: boolean;
}) => {
  return lineItems.map((lineItem, childIndex) => {
    const purchaseMetadata = getPurchaseMetadataEntries(lineItem?.orderLineItemsPurchaseMetadata);
    if (shouldGroupApps) {
      const productRow = (
        <OrderLineItemRow
          testId={`ppc-order-history-${order?.orderNumber}-line-item-row-${childIndex}`}
          transactionAccountId={transactionAccountId}
          key={`order-history-txa-${lineItem?.lineItemId}`}
          lineItem={lineItem as OrderLineItem}
          purchaseMetadata={purchaseMetadata}
          order={order}
          invoiceGroup={invoiceGroup}
          isFlatDiscountSupportEnabled={isFlatDiscountSupportEnabled}
          visibleColumns={visibleColumns}
          subscriptionCellVersion={subscriptionCellVersion}
          shouldShowLegacyInvoiceId={shouldShowLegacyInvoiceId}
          hasIntegratedApps={(lineItem as any)?.apps?.length > 0}
          shouldTopAlignIcon={true}
          isSandboxSupportForSubscriptionsEnabled={isSandboxSupportForSubscriptionsEnabled}
          isManualBillingSupportEnabled={true}
          isGracePeriodVisibilityEnabled={isGracePeriodVisibilityEnabled}
        />
      );

      const appRows = (lineItem as any)?.apps?.map((app: any, appIndex: any) => (
        <OrderLineItemRow
          testId={`ppc-order-history-${order?.orderNumber}-line-item-app-row-${appIndex}`}
          transactionAccountId={transactionAccountId}
          key={`order-history-txa-${app?.lineItemId}`}
          lineItem={app as OrderLineItem}
          purchaseMetadata={purchaseMetadata}
          order={order}
          invoiceGroup={invoiceGroup}
          isFlatDiscountSupportEnabled={isFlatDiscountSupportEnabled}
          visibleColumns={visibleColumns}
          subscriptionCellVersion={subscriptionCellVersion}
          shouldShowLegacyInvoiceId={shouldShowLegacyInvoiceId}
          isApp={true}
          shouldTopAlignIcon={true}
          isSandboxSupportForSubscriptionsEnabled={isSandboxSupportForSubscriptionsEnabled}
          isGracePeriodVisibilityEnabled={isGracePeriodVisibilityEnabled}
        />
      ));

      const renderApps = () => {
        const appsCount = appRows?.length ?? 0;

        if (!appsCount) {
          return null;
        }

        return (
          <ExpandableRow
            testId={`expandable-row`}
            rowsAsCells={[
              [
                <td className="expandable-row-header flex align-center gap2" key={`products-${childIndex}`}>
                  <Badge>
                    {appsCount} installed {appsCount === 1 ? `app` : `apps`}
                  </Badge>
                </td>,
                <td key="dummy-column" colSpan={visibleColumns?.length}></td>,
              ],
            ]}
            subRows={appRows}
            className="customized-apps-expandable-row"
          />
        );
      };

      return (
        <>
          {productRow}
          {renderApps()}
        </>
      );
    }

    return (
      <OrderLineItemRow
        testId={`ppc-order-history-${order?.orderNumber}-line-item-row-${childIndex}`}
        transactionAccountId={transactionAccountId}
        key={`order-history-txa-${lineItem?.lineItemId}`}
        lineItem={lineItem as OrderLineItem}
        purchaseMetadata={purchaseMetadata}
        order={order}
        invoiceGroup={invoiceGroup}
        isFlatDiscountSupportEnabled={isFlatDiscountSupportEnabled}
        visibleColumns={visibleColumns}
        subscriptionCellVersion={subscriptionCellVersion}
        shouldShowLegacyInvoiceId={shouldShowLegacyInvoiceId}
        isSandboxSupportForSubscriptionsEnabled={isSandboxSupportForSubscriptionsEnabled}
        isManualBillingSupportEnabled={true}
        isGracePeriodVisibilityEnabled={isGracePeriodVisibilityEnabled}
      />
    );
  });
};

const headersMap = {
  [OrderHistoryTableColumns.SUBSCRIPTION]: {
    name: OrderHistoryTableColumns.SUBSCRIPTION,
    content: OrderHistoryTableColumns.SUBSCRIPTION,
    customClassNames: 'col-subscription',
  },
  [OrderHistoryTableColumns.PLAN]: {
    name: OrderHistoryTableColumns.PLAN,
    content: OrderHistoryTableColumns.PLAN,
    customClassNames: 'col-plan',
  },
  [OrderHistoryTableColumns.ORDER_DATE]: {
    name: OrderHistoryTableColumns.ORDER_DATE,
    content: OrderHistoryTableColumns.ORDER_DATE,
    customClassNames: 'col-order-date',
  },
  [OrderHistoryTableColumns.COMPANY]: {
    name: OrderHistoryTableColumns.COMPANY,
    content: OrderHistoryTableColumns.COMPANY,
    customClassNames: 'col-company',
  },
  [OrderHistoryTableColumns.SOLD_TO]: {
    name: OrderHistoryTableColumns.SOLD_TO,
    content: OrderHistoryTableColumns.SOLD_TO,
    customClassNames: 'col-sold-to-address',
  },
  [OrderHistoryTableColumns.SITE]: {
    name: OrderHistoryTableColumns.SITE,
    content: OrderHistoryTableColumns.SITE,
    customClassNames: 'col-site',
  },
  [OrderHistoryTableColumns.ORDER_SUMMARY]: {
    name: OrderHistoryTableColumns.ORDER_SUMMARY,
    content: OrderHistoryTableColumns.ORDER_SUMMARY,
    customClassNames: 'col-order-summary',
  },
  [OrderHistoryTableColumns.ORDER_TYPE]: {
    name: OrderHistoryTableColumns.ORDER_TYPE,
    content: OrderHistoryTableColumns.ORDER_TYPE,
    customClassNames: 'col-order-type',
  },
  [OrderHistoryTableColumns.NOTE]: {
    name: OrderHistoryTableColumns.NOTE,
    content: OrderHistoryTableColumns.NOTE,
    customClassNames: 'col-common',
  },
  [OrderHistoryTableColumns.SUPPORT_TICKET]: {
    name: OrderHistoryTableColumns.SUPPORT_TICKET,
    content: OrderHistoryTableColumns.SUPPORT_TICKET,
    customClassNames: 'col-common',
  },
  [OrderHistoryTableColumns.PLACED_BY]: {
    name: OrderHistoryTableColumns.PLACED_BY,
    content: OrderHistoryTableColumns.PLACED_BY,
    customClassNames: 'col-common',
  },
  [OrderHistoryTableColumns.CREATED_BY]: {
    name: OrderHistoryTableColumns.CREATED_BY,
    content: OrderHistoryTableColumns.CREATED_BY,
    customClassNames: 'col-common',
  },
};

export const getOrderHistoryTableHeaders = (
  isDesignIteration1Enabled: boolean,
  columnsToShow: OrderHistoryTableColumns[]
) => {
  if (columnsToShow?.length > 0) {
    const columns = columnsToShow?.map((column: OrderHistoryTableColumns) => {
      if (column === OrderHistoryTableColumns.SUBSCRIPTION) {
        return { ...headersMap[column], customClassNames: 'col-subscription-v2' };
      }
      return headersMap[column];
    });
    return columns;
  } else if (isDesignIteration1Enabled) {
    return [
      headersMap[OrderHistoryTableColumns.SUBSCRIPTION],
      headersMap[OrderHistoryTableColumns.PLAN],
      headersMap[OrderHistoryTableColumns.ORDER_DATE],
      headersMap[OrderHistoryTableColumns.ORDER_TYPE],
      headersMap[OrderHistoryTableColumns.CREATED_BY],
    ];
  }

  return [
    headersMap[OrderHistoryTableColumns.SUBSCRIPTION],
    headersMap[OrderHistoryTableColumns.PLAN],
    headersMap[OrderHistoryTableColumns.ORDER_DATE],
    headersMap[OrderHistoryTableColumns.COMPANY],
    headersMap[OrderHistoryTableColumns.SOLD_TO],
    headersMap[OrderHistoryTableColumns.SITE],
    headersMap[OrderHistoryTableColumns.ORDER_SUMMARY],
    headersMap[OrderHistoryTableColumns.NOTE],
    headersMap[OrderHistoryTableColumns.SUPPORT_TICKET],
    headersMap[OrderHistoryTableColumns.PLACED_BY],
  ];
};

const initialTableData = {
  totalCount: 0,
  nodes: [],
};

export const OrderHistoryTable = ({
  transactionAccountId,
  label = 'Order history',
  testId = 'ppc-order-history-table',
  filters = [],
  isSearchHidden = false,
  error,
  shouldNotGroupRows = false,
  isExportingCSVHidden = false,
  emptyStateProp,
  emptyStatePropForSearch,
  shouldHideFilters,
  searchTerm,
  columnsToShow = [],
  shouldEnableFrontEndSearch = false,
}: OrderHistoryTableProps) => {
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [filtersAfterSearch, setFiltersAfterSearch] = useState<FilterOption[]>([]);
  const [tableData, setTableData] = useState<Partial<generatedTypesNew.OrderConnection>>(initialTableData);
  const [isTableLoading, setIsTableLoading] = useState<boolean>(true);
  const [isSearched, setIsSearched] = useState(!!searchTerm);
  const [csvFilterOptions, setCsvFilterOptions] = useState<FilterOption | FilterOption[]>(filters);
  const { orderId } = useParams<{ orderId: string }>();
  const [searchedLineItems, setSearchedLineItems] = useState<OrderLineItem[]>([]);
  const [searchInput, setSearchInput] = useState('');

  const { data, loading, error: ordersError, refetch: refetchOrders } = useFetchOrdersForTransactionAccountPPCQuery({
    transactionAccountId,
    filterOptions: filters,
  });

  const refetch = useCallback(
    (variables) => {
      setCsvFilterOptions(variables.filterOptions);
      refetchOrders(variables);
    },
    [refetchOrders]
  );

  const [isEnabled] = useFeatureToggle();
  const isFlatDiscountSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_FLAT_DISCOUNT_SUPPORT);
  const isStepUpSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_STEP_UP_CREDIT_SUPPORT);
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );
  const isGracePeriodVisibilityEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_GRACE_PERIOD_VISIBILITY);

  const { downloadOrdersDataCSVv2, ordersDataLoading } = useOrderHistoryCSV(
    transactionAccountId,
    csvFilterOptions,
    isFlatDiscountSupportEnabled
  );

  const ordersData = useMemo(() => {
    return data?.transactionAccount?.orders ?? {};
  }, [data]);

  const availableInvoiceGroups = data?.transactionAccount?.availableInvoiceGroups?.nodes ?? [];
  const totalCount = tableData?.totalCount ?? 0;

  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  /** only group apps under products when its new design as well as if its rendering on order details screen */
  const shouldGroupApps = isDesignIteration1Enabled && columnsToShow?.length > 0;

  const getFilteredLineItems = (order: Order | null | undefined, searchInput: string | null) => {
    const regexPattern = `\\s*${searchInput?.trim()}\\s*`;
    const orderLineItems = order?.orderLineItems?.nodes as OrderLineItem[];

    return orderLineItems?.filter((lineItem: OrderLineItem) => {
      const hasEntitlementNumberMatched = new RegExp(regexPattern, 'ig').test(lineItem?.entitlementNumber as string);
      return hasEntitlementNumberMatched;
    });
  };

  useEffect(() => {
    setTableData(ordersData);
    setIsTableLoading(loading);
  }, [ordersData, loading]);

  const onPageChange = useCallback(
    (newPageNumber: number) => {
      if (newPageNumber === currentPage) return;

      setCurrentPage(newPageNumber);
      refetch({
        filterOptions: {
          and: [
            ...filters,
            {
              or: filtersAfterSearch,
            },
          ],
        },
        searchOptions: {
          first: rowsPerPage,
          offset: (newPageNumber - 1) * rowsPerPage,
        },
      });
    },
    [refetch, rowsPerPage, currentPage, filters, filtersAfterSearch]
  );

  const handleRowsChange = useCallback(
    (newRowPerPage: number) => {
      if (newRowPerPage === rowsPerPage) return;

      setCurrentPage(DEFAULT_PAGE);
      setRowsPerPage(newRowPerPage);
      refetch({
        filterOptions: {
          and: [
            ...filters,
            {
              or: filtersAfterSearch,
            },
          ],
        },
        searchOptions: {
          first: newRowPerPage,
          offset: 0,
        },
      });
    },
    [refetch, rowsPerPage, filters, filtersAfterSearch]
  );

  const searchOptionsForDependentQueries = useMemo(() => {
    return {
      first: maxThresholdForResults,
      offset: 0,
    };
  }, []);
  const searchOptionsForOrdersQueries = useMemo(() => {
    return {
      first: rowsPerPage,
      offset: 0,
    };
  }, [rowsPerPage]);

  const onFrontEndSearch = useCallback(
    (formData: FormData) => {
      const searchedInput = formData?.searchTerm;
      setIsSearched(!!searchedInput);
      setSearchInput(searchedInput);
      const requiredOrder = (ordersData as OrderConnection)?.nodes?.find((order) => order?.id === orderId);
      const filteredLineItems = getFilteredLineItems(requiredOrder, searchedInput);
      setSearchedLineItems(filteredLineItems || []);
    },
    [searchInput, ordersData, tableData, searchedLineItems]
  );

  const getOrdersDataForCloudSite = useCallback(
    async (searchInput: string) => {
      if (!searchInput || !transactionAccountId) {
        // Note: Skip key is not working hence using the explicit check
        return initialTableData;
      }

      // Currently, there is no direct access of cloud site entity at Order level.
      // So, we first fetch all the matching cloud sites for a TXA, then
      // fetch all the orders based on the `orderLineItemsCloudSiteId`.
      const { data } = await graphClient().query({
        query: fetchCloudSitesForTXA,
        variables: {
          id: transactionAccountId,
          searchOptions: searchOptionsForDependentQueries,
          filterOptions: {
            or: [
              {
                key: 'cloudSiteHostname',
                value: searchInput,
              },
            ],
          },
        },
      });

      const cloudSites = data?.cloudSitesForTransactionAccount ?? [];

      if (!cloudSites?.length) {
        // Note: Skip key is not working hence using the explicit check
        return initialTableData;
      }

      const orFilter = cloudSites.map((cloudSite: generatedTypesNew.CloudSiteDetail) => {
        return {
          key: 'orderLineItemsCloudSiteId',
          value: cloudSite?.cloudSiteId,
        };
      });

      setFiltersAfterSearch(orFilter);

      const filterOptions = {
        and: [
          ...filters,
          {
            or: orFilter,
          },
        ],
      };

      setCsvFilterOptions(filterOptions);

      const { data: ordersData } = await graphClient().query({
        query: fetchOrdersForTransactionAccountInPPC,
        variables: {
          transactionAccountId,
          searchOptions: searchOptionsForOrdersQueries,
          filterOptions,
        },
      });

      return ordersData?.transactionAccount?.orders ?? {};
    },
    [filters, transactionAccountId, searchOptionsForDependentQueries, searchOptionsForOrdersQueries]
  );

  const getOrdersDataForCompanyAndSoldToAddress = useCallback(
    async (searchInput: string) => {
      if (!searchInput || !transactionAccountId) {
        // Note: Skip key is not working hence using the explicit check
        return initialTableData;
      }

      // Currently, there is no direct access of sold-to-address entity at Order level.
      // So, we first fetch all the invoice groups based on `shipToName`, `shipToAddress1`, `shipToAddress2`, `shipToCity`, `shipToState`, `shipToCountry` and `shipToPostalCode` then
      // fetch all the orders based on the invoice group ids (`billingGroupId`).
      const { data } = await graphClient().query({
        query: fetchInvoiceGroupsForSearch,
        variables: {
          transactionAccountId,
          searchOptions: searchOptionsForDependentQueries,
          filterOptions: getFilterOptionsForInvoiceGroupsQuery(transactionAccountId, searchInput),
        },
      });

      const invoiceGroups = data?.invoiceGroups?.nodes ?? [];

      if (!invoiceGroups?.length) {
        // Note: Skip key is not working hence using the explicit check
        return initialTableData;
      }

      const orFilter = invoiceGroups.map((invoiceGroup: generatedTypesNew.InvoiceGroup) => {
        return {
          key: 'billingGroupId',
          value: invoiceGroup?.id,
        };
      });

      setFiltersAfterSearch(orFilter);

      const filterOptions = {
        and: [
          ...filters,
          {
            or: orFilter,
          },
        ],
      };

      setCsvFilterOptions(filterOptions);

      const { data: ordersData } = await graphClient().query({
        query: fetchOrdersForTransactionAccountInPPC,
        variables: {
          transactionAccountId,
          searchOptions: searchOptionsForOrdersQueries,
          filterOptions,
        },
      });

      return ordersData?.transactionAccount?.orders ?? {};
    },
    [filters, transactionAccountId, searchOptionsForDependentQueries, searchOptionsForOrdersQueries]
  );

  const fetchAppropriateOrdersData = useCallback(
    async (searchInput: string, isCloudSite?: boolean) => {
      setIsTableLoading(true);
      const ordersData = isCloudSite
        ? await getOrdersDataForCloudSite(searchInput)
        : await getOrdersDataForCompanyAndSoldToAddress(searchInput);
      setTableData(ordersData);
      setIsTableLoading(false);
    },
    [getOrdersDataForCloudSite, getOrdersDataForCompanyAndSoldToAddress]
  );

  const handleSearch = useCallback(
    async (formData: FormData) => {
      const searchInput = formData?.searchTerm;
      setIsSearched(!!searchInput);
      const isValidOrderNumber = searchInput?.match(RegexPatterns.ORDER);
      const allCloudSiteRegexPatterns = [
        RegexPatterns.CLOUD_SITE_BITBUCKET,
        RegexPatterns.CLOUD_SITE_PROD,
        RegexPatterns.CLOUD_SITE_STG,
        RegexPatterns.CLOUD_SITE_STATUSPAGE_PROD,
        RegexPatterns.CLOUD_SITE_STATUSPAGE_STG,
      ];
      const isValidCloudSite = allCloudSiteRegexPatterns.some((regexPattern) => searchInput?.match(regexPattern));

      setCurrentPage(DEFAULT_PAGE);

      if (isValidOrderNumber || searchInput === '') {
        // Refetch for valid order number or when the search is cleared
        refetch({
          filterOptions: searchInput
            ? [
                ...filters,
                {
                  key: 'orderNumber',
                  // Upper-cased to enable case-insensitive search as orderNumber is always in uppercase
                  value: searchInput?.toUpperCase(),
                },
              ]
            : filters,
          searchOptions: searchOptionsForOrdersQueries,
        });
      } else if (isValidCloudSite) {
        fetchAppropriateOrdersData(searchInput, true);
      } else {
        fetchAppropriateOrdersData(searchInput);
      }
    },
    [refetch, filters, fetchAppropriateOrdersData, searchOptionsForOrdersQueries]
  );

  const renderCustomSearch = () => {
    const SEARCH_BOX_HELPER_MESSAGE = 'Enter order-#, company name, sold-to address or site URL';
    const SEARCH_PLACEHOLDER_TEXT = 'Search';

    return (
      <CustomSearch
        placeholder={SEARCH_PLACEHOLDER_TEXT}
        onSearch={shouldEnableFrontEndSearch ? onFrontEndSearch : handleSearch}
        helperMessage={shouldEnableFrontEndSearch ? 'Enter E#' : SEARCH_BOX_HELPER_MESSAGE}
        isLoading={isTableLoading}
        isClearable={true}
      />
    );
  };

  const renderFilters = () => {
    return <div className="order-history-table-filters">{isSearchHidden || renderCustomSearch()}</div>;
  };
  const tableDataLength = tableData?.nodes?.length ?? 0;

  const tableColumns = getOrderHistoryTableHeaders(isDesignIteration1Enabled, columnsToShow);
  const tableColumnsNames = tableColumns.map((column) => column.name);
  const subscriptionCellVersion = isDesignIteration1Enabled ? 2 : undefined;

  const derivedEmptyStateProp = isDesignIteration1Enabled
    ? emptyStateProp || emptyStatePropForOrderHistoryTable
    : emptyStateProp || defaultEmptyStateProp;

  const derivedEmptyStatePropForSearch = isDesignIteration1Enabled
    ? emptyStatePropForSearch || emptyStatePropForWrongSearch
    : emptyStatePropForSearch || defaultEmptyStateProp;

  const shouldShowLegacyInvoiceId = isStepUpSupportEnabled;
  const shouldRenderFrontEndSearchResult = shouldEnableFrontEndSearch && isSearched;

  return (
    <CustomDynamicResultTable
      className={classNames('ppc-order-history-table', {
        'ppc-order-history-table-v2': isDesignIteration1Enabled,
      })}
      testId={testId}
      isLoading={isTableLoading}
      error={ordersError || error?.toString()}
      noResults={shouldRenderFrontEndSearchResult ? searchedLineItems.length < 1 : tableDataLength < 1}
      emptyStateProps={isSearched ? derivedEmptyStatePropForSearch : derivedEmptyStateProp}
      totalCount={totalCount}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      isPaginationEnabled={tableDataLength > 0 && totalCount > DEFAULT_ROWS_PER_PAGE}
      onRowChange={handleRowsChange}
      shouldShowHorizontalScrollAnimation={!isDesignIteration1Enabled}
      hasTableWrapper={!isDesignIteration1Enabled}
      errorStateProps={errorStateProp}
      innerHeading={!isDesignIteration1Enabled && <TableHeading>{label}</TableHeading>}
      filters={renderFilters()}
      shouldForceEnableFilters={true}
      isSearched={isSearched}
      customiseColumnView={
        !isExportingCSVHidden ? (
          <LoadingButton
            onClick={() => {
              downloadOrdersDataCSVv2(totalCount);
            }}
            testId="order-history-export-as-csv"
            isLoading={ordersDataLoading || isTableLoading}
            iconBefore={<ExportIcon label="Export icon" size="small" />}
          >
            Export as CSV
          </LoadingButton>
        ) : undefined
      }
      loaderOptions={{
        filtersCount: 0,
        hideActions: isDesignIteration1Enabled,
        ...(shouldHideFilters && SEARCH_PAGE_LOADER_OPTIONS),
      }}
    >
      <ColGroup columns={tableColumns} />
      <TableHead columns={tableColumns} />
      <TableBody>
        {tableData?.nodes?.map((node: generatedTypesNew.Order | null, index: number) => {
          function getOrderHeaderRow() {
            const orderDetailsRoutePPC = getRouteUrl(routePaths.orderDetails, {
              txaId: transactionAccountId || '',
              orderId: node?.id as string,
            });

            if (isDesignIteration1Enabled) {
              const supportTicket = node?.metadata?.supportTicketNumber;

              return [
                [
                  <td className="order-number-heading-wrapper" key={`order-number-heading-${node?.orderNumber}`}>
                    <span>Order number&nbsp;</span>
                    <Link
                      data-testid="order-number-link"
                      to={orderDetailsRoutePPC}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {node?.orderNumber}
                    </Link>
                    <br />
                    {supportTicket && (
                      <span>
                        Support ticket&nbsp;
                        <SupportTicketNumber supportTicket={supportTicket} />
                      </span>
                    )}
                  </td>,
                ],
              ];
            }

            return [
              [
                <td className={'ppc-order-number-label'} key={`order-number-heading-${node?.orderNumber}`}>
                  <h3>Order number</h3>
                </td>,
                <td className={'drop-shadow'} key={`order-number-value-${node?.orderNumber}`} colSpan={3}>
                  <Link
                    className={`ppc-order-history-table__order-number-link`}
                    data-testid={`ppc-order-history-table__order-number-link`}
                    to={orderDetailsRoutePPC}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h3 className={'ppc-order-history-table__order-number'}>{node?.orderNumber}</h3>
                  </Link>
                </td>,
                // eslint-disable-next-line react/jsx-key
                <td className={'dummy-column'} colSpan={7}></td>,
              ],
            ];
          }

          const getInvoiceGroup = (invoiceGroupId: string | undefined | null) => {
            if (invoiceGroupId) {
              return availableInvoiceGroups?.find((invoiceGroup) => {
                return invoiceGroup?.id === invoiceGroupId;
              });
            }
          };

          if (shouldNotGroupRows) {
            return (
              <>
                {getOrderLineItemRows({
                  order: node as Order,
                  transactionAccountId,
                  invoiceGroup: getInvoiceGroup(node?.billingGroup?.billingGroupId),
                  isFlatDiscountSupportEnabled,
                  visibleColumns: tableColumnsNames,
                  subscriptionCellVersion,
                  shouldShowLegacyInvoiceId,
                  shouldGroupApps,
                  searchedLineItems,
                  shouldRenderFrontEndSearchResult,
                  isSandboxSupportForSubscriptionsEnabled,
                  isGracePeriodVisibilityEnabled,
                })}
              </>
            );
          }

          return (
            <ExpandableRow
              testId={`ppc-order-history-expandable-row-${index}`}
              key={`expandable-row-key-${node?.orderNumber}`}
              isDefaultExpanded={true}
              rowsAsCells={getOrderHeaderRow()}
              subRows={getOrderLineItemRows({
                order: node as Order,
                transactionAccountId,
                invoiceGroup: getInvoiceGroup(node?.billingGroup?.billingGroupId),
                isFlatDiscountSupportEnabled,
                visibleColumns: tableColumnsNames,
                subscriptionCellVersion,
                shouldShowLegacyInvoiceId,
                isSandboxSupportForSubscriptionsEnabled,
                isGracePeriodVisibilityEnabled,
              })}
            />
          );
        })}
      </TableBody>
    </CustomDynamicResultTable>
  );
};
